import { apiClient } from './apiClient';
import { logger } from '../../utils/logger';
import type {
  ContentRecommendation,
  RecommendationFeedback,
  ContextualFactor,
  BanditsState,
  ContentType,
} from '@coffeeplexity/shared';

export interface FetchRecommendationsParams {
  userId: string;
  limit?: number;
  contentTypes?: ContentType[];
  excludeIds?: string[];
  context?: Record<string, any>;
  skillCategories?: string[];
  difficultyRange?: [number, number];
  timeRange?: [number, number];
  confidenceThreshold?: number;
}

export interface RecommendationResponse {
  recommendations: ContentRecommendation[];
  totalCount: number;
  hasMore: boolean;
  nextCursor?: string;
  cacheExpiry: string;
  metadata: {
    generatedAt: string;
    modelVersion: string;
    contextFactors: string[];
    personalizationScore: number;
  };
}

export interface FeedbackSubmissionResponse {
  feedback: RecommendationFeedback;
  impactedRecommendations: string[];
  modelUpdateScheduled: boolean;
  nextRecommendationRefresh: string;
}

export interface ContextUpdateResponse {
  factors: ContextualFactor[];
  impactedRecommendations: string[];
  optimizationSuggestions: string[];
}

export interface BanditsExplorationResponse {
  recommendation: ContentRecommendation;
  armId: string;
  explorationReason: string;
  confidenceInterval: [number, number];
}

export interface RecommendationAnalytics {
  recommendationId: string;
  viewCount: number;
  acceptanceRate: number;
  averageRating: number;
  similarUserEngagement: number;
  performanceScore: number;
  conversionRate: number;
  timeToAction: number;
  feedbackSentiment: number;
}

export interface CacheManagementResponse {
  cacheStatus: 'hit' | 'miss' | 'stale' | 'expired';
  ttlRemaining: number;
  cacheSize: number;
  evictionCount: number;
  prefetchScheduled: boolean;
}

class RecommendationApiService {
  private readonly baseUrl = '/ai/recommendations';
  private readonly cachePrefix = 'rec_cache_';
  private readonly maxRetries = 3;
  private readonly retryDelay = 1000;

  /**
   * Fetch personalized content recommendations
   */
  async fetchRecommendations(
    params: FetchRecommendationsParams
  ): Promise<RecommendationResponse> {
    try {
      const queryParams = new URLSearchParams({
        userId: params.userId,
        limit: (params.limit || 10).toString(),
        ...(params.contentTypes && {
          contentTypes: params.contentTypes.join(','),
        }),
        ...(params.excludeIds && {
          excludeIds: params.excludeIds.join(','),
        }),
        ...(params.context && {
          context: JSON.stringify(params.context),
        }),
        ...(params.skillCategories && {
          skillCategories: params.skillCategories.join(','),
        }),
        ...(params.difficultyRange && {
          difficultyMin: params.difficultyRange[0].toString(),
          difficultyMax: params.difficultyRange[1].toString(),
        }),
        ...(params.timeRange && {
          timeMin: params.timeRange[0].toString(),
          timeMax: params.timeRange[1].toString(),
        }),
        ...(params.confidenceThreshold && {
          confidenceThreshold: params.confidenceThreshold.toString(),
        }),
      });

      const response = await apiClient.get(
        `${this.baseUrl}?${queryParams.toString()}`
      );

      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch recommendations:', error);
      throw this.handleApiError(error, 'fetch_recommendations');
    }
  }

  /**
   * Submit recommendation feedback with immediate updates
   */
  async submitFeedback(
    feedback: Omit<RecommendationFeedback, 'id' | 'timestamp'>
  ): Promise<FeedbackSubmissionResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/feedback`, {
        ...feedback,
        timestamp: new Date().toISOString(),
      });

      // Trigger real-time recommendation updates if needed
      if (response.data.modelUpdateScheduled) {
        this.scheduleRecommendationRefresh(
          feedback.userId,
          response.data.nextRecommendationRefresh
        );
      }

      return response.data;
    } catch (error: any) {
      logger.error('Failed to submit feedback:', error);
      throw this.handleApiError(error, 'submit_feedback');
    }
  }

  /**
   * Update contextual factors for better recommendations
   */
  async updateContextualFactors(
    userId: string,
    factors: Partial<ContextualFactor>[]
  ): Promise<ContextUpdateResponse> {
    try {
      const response = await apiClient.put(`${this.baseUrl}/context`, {
        userId,
        factors: factors.map(factor => ({
          ...factor,
          lastUpdated: new Date().toISOString(),
        })),
      });

      return response.data;
    } catch (error: any) {
      logger.error('Failed to update contextual factors:', error);
      throw this.handleApiError(error, 'update_context');
    }
  }

  /**
   * Fetch multi-armed bandit state for exploration
   */
  async fetchBanditsState(userId: string): Promise<BanditsState> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/bandits/${userId}`);

      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch bandits state:', error);
      throw this.handleApiError(error, 'fetch_bandits');
    }
  }

  /**
   * Explore a specific recommendation arm
   */
  async exploreRecommendation(
    userId: string,
    armId: string,
    context?: Record<string, any>
  ): Promise<BanditsExplorationResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/explore`, {
        userId,
        armId,
        context: {
          ...context,
          exploredAt: new Date().toISOString(),
        },
      });

      return response.data;
    } catch (error: any) {
      logger.error('Failed to explore recommendation:', error);
      throw this.handleApiError(error, 'explore_recommendation');
    }
  }

  /**
   * Get recommendation analytics and performance data
   */
  async getRecommendationAnalytics(
    recommendationId: string
  ): Promise<RecommendationAnalytics> {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/analytics/${recommendationId}`
      );

      return response.data;
    } catch (error: any) {
      logger.error('Failed to get recommendation analytics:', error);
      throw this.handleApiError(error, 'get_analytics');
    }
  }

  /**
   * Batch get analytics for multiple recommendations
   */
  async getBatchRecommendationAnalytics(
    recommendationIds: string[]
  ): Promise<Record<string, RecommendationAnalytics>> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/analytics/batch`, {
        recommendationIds,
      });

      return response.data;
    } catch (error: any) {
      logger.error('Failed to get batch recommendation analytics:', error);
      throw this.handleApiError(error, 'get_batch_analytics');
    }
  }

  /**
   * Refresh recommendations with cache management
   */
  async refreshRecommendations(
    userId: string,
    forceRefresh = false
  ): Promise<RecommendationResponse> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/refresh`, {
        userId,
        forceRefresh,
        timestamp: new Date().toISOString(),
      });

      // Update local cache metadata
      this.updateCacheMetadata(userId, response.data.cacheExpiry);

      return response.data;
    } catch (error: any) {
      logger.error('Failed to refresh recommendations:', error);
      throw this.handleApiError(error, 'refresh_recommendations');
    }
  }

  /**
   * Get cache status and management info
   */
  async getCacheStatus(userId: string): Promise<CacheManagementResponse> {
    try {
      const response = await apiClient.get(
        `${this.baseUrl}/cache/status/${userId}`
      );

      return response.data;
    } catch (error: any) {
      logger.error('Failed to get cache status:', error);
      throw this.handleApiError(error, 'get_cache_status');
    }
  }

  /**
   * Invalidate recommendation cache
   */
  async invalidateCache(
    userId: string,
    pattern?: string
  ): Promise<{ invalidated: boolean; count: number }> {
    try {
      const response = await apiClient.delete(
        `${this.baseUrl}/cache/${userId}`,
        {
          data: { pattern },
        }
      );

      return response.data;
    } catch (error: any) {
      logger.error('Failed to invalidate cache:', error);
      throw this.handleApiError(error, 'invalidate_cache');
    }
  }

  /**
   * Prefetch recommendations for improved performance
   */
  async prefetchRecommendations(
    userId: string,
    context?: Record<string, any>
  ): Promise<{ prefetched: boolean; count: number; cacheExpiry: string }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/prefetch`, {
        userId,
        context: {
          ...context,
          prefetchedAt: new Date().toISOString(),
        },
      });

      return response.data;
    } catch (error: any) {
      logger.error('Failed to prefetch recommendations:', error);
      throw this.handleApiError(error, 'prefetch_recommendations');
    }
  }

  /**
   * Track recommendation view for analytics
   */
  async trackRecommendationView(
    recommendationId: string,
    userId: string,
    viewDuration: number,
    context?: Record<string, any>
  ): Promise<{ tracked: boolean }> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/track/view`, {
        recommendationId,
        userId,
        viewDuration,
        context: {
          ...context,
          viewedAt: new Date().toISOString(),
        },
      });

      return response.data;
    } catch (error: any) {
      logger.error('Failed to track recommendation view:', error);
      // Don't throw for tracking errors, just log them
      return { tracked: false };
    }
  }

  /**
   * Track recommendation interaction for analytics
   */
  async trackRecommendationInteraction(
    recommendationId: string,
    userId: string,
    interactionType: string,
    metadata?: Record<string, any>
  ): Promise<{ tracked: boolean }> {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/track/interaction`,
        {
          recommendationId,
          userId,
          interactionType,
          metadata: {
            ...metadata,
            interactedAt: new Date().toISOString(),
          },
        }
      );

      return response.data;
    } catch (error: any) {
      logger.error('Failed to track recommendation interaction:', error);
      // Don't throw for tracking errors, just log them
      return { tracked: false };
    }
  }

  /**
   * Get recommendation performance metrics
   */
  async getPerformanceMetrics(
    userId: string,
    timeRange?: { start: string; end: string }
  ): Promise<{
    loadTime: number;
    cacheHitRate: number;
    errorRate: number;
    userEngagement: number;
    conversionRate: number;
    averageConfidence: number;
  }> {
    try {
      const params = new URLSearchParams({
        userId,
        ...(timeRange && {
          startTime: timeRange.start,
          endTime: timeRange.end,
        }),
      });

      const response = await apiClient.get(
        `${this.baseUrl}/metrics/performance?${params.toString()}`
      );

      return response.data;
    } catch (error: any) {
      logger.error('Failed to get performance metrics:', error);
      throw this.handleApiError(error, 'get_performance_metrics');
    }
  }

  /**
   * Private helper methods
   */
  private async scheduleRecommendationRefresh(
    userId: string,
    refreshTime: string
  ): Promise<void> {
    const refreshDate = new Date(refreshTime);
    const delay = refreshDate.getTime() - Date.now();

    if (delay > 0 && delay < 5 * 60 * 1000) {
      // Only schedule if within 5 minutes
      setTimeout(() => {
        this.refreshRecommendations(userId, true).catch(error => {
          logger.error('Scheduled refresh failed:', error);
        });
      }, delay);
    }
  }

  private updateCacheMetadata(userId: string, cacheExpiry: string): void {
    try {
      const cacheKey = `${this.cachePrefix}${userId}`;
      const metadata = {
        expiry: cacheExpiry,
        updatedAt: new Date().toISOString(),
      };

      // Store in local storage or cache system
      // This would typically use AsyncStorage or a cache library
      logger.log(`Cache metadata updated for ${cacheKey}:`, metadata);
    } catch (error) {
      logger.error('Failed to update cache metadata:', error);
    }
  }

  private handleApiError(error: any, operation: string): Error {
    const errorMessage =
      error.response?.data?.detail || error.message || 'Unknown error';
    const errorCode = error.response?.status || 'UNKNOWN';

    // Create structured error for better handling
    const structuredError = new Error(`${operation}: ${errorMessage}`);
    (structuredError as any).code = errorCode;
    (structuredError as any).operation = operation;
    (structuredError as any).recoverable = errorCode < 500;
    (structuredError as any).retryable = errorCode >= 500 || errorCode === 429;

    return structuredError;
  }

  /**
   * Retry mechanism for failed requests
   */
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries = this.maxRetries
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (retries > 0 && (error as any).retryable) {
        await this.delay(this.retryDelay);
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const recommendationApi = new RecommendationApiService();
